/**
 * P4 — shared fetch helpers for the client-report agency API modules.
 *
 * The P3 `client-reports-api.ts` keeps its own private copies of these (it is
 * frozen — additive-only). New P4 modules (report-branding, KPI/targets) import
 * from here instead of re-declaring the wrapper, so the request shape stays in
 * lockstep across the portal surface.
 *
 * Same contract as roles-api / identity-api:
 *   - Bearer = the agency `emp_` key from Redux (`auth.agency.accessToken`).
 *   - `credentials: 'include'` so the httpOnly agency-session cookie also
 *     authenticates on a cold reload.
 *   - `{ data }` envelope unwrapped by callers; enriched `Error & { status,
 *     code, validation }` thrown on non-2xx.
 *   - Base URL `CENTRAL_API_BASE_URL` (= `${env}/api/v1/`).
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

export interface PortalApiError extends Error {
  status?: number;
  code?: string;
  validation?: Record<string, string[]>;
}

/** Auth header only (no Content-Type — set per request / omitted for FormData). */
export function getAuthHeader(): Record<string, string> {
  const token = store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[client-reports-http] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

export async function throwEnriched(response: Response): Promise<never> {
  let detail = '';
  let code: string | undefined;
  let validation: Record<string, string[]> | undefined;
  try {
    const body = await parseJson<{
      message?: string;
      error?: string;
      errors?: Record<string, string[]>;
    }>(response);
    code = body?.error;
    detail = body?.message || body?.error || '';
    validation = body?.errors;
  } catch {
    /* fall back to status text */
  }
  const err = new Error(
    detail || `Request failed (${response.status} ${response.statusText})`
  ) as PortalApiError;
  err.status = response.status;
  err.code = code;
  err.validation = validation;
  throw err;
}

/** JSON request wrapper (auto Content-Type: application/json when a body set). */
export async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!response.ok) await throwEnriched(response);
  return parseJson<T>(response);
}

/**
 * Multipart request (asset uploads). NEVER set Content-Type — the browser adds
 * the multipart boundary. Auth header only.
 */
export async function requestMultipart<T>(
  path: string,
  form: FormData,
  method = 'POST'
): Promise<T> {
  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    method,
    headers: getAuthHeader(),
    credentials: 'include',
    body: form,
  });
  if (!response.ok) await throwEnriched(response);
  return parseJson<T>(response);
}

export function toQuery(
  params: Record<string, string | number | undefined | null>
): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) usp.append(k, String(v));
  });
  const q = usp.toString();
  return q ? `?${q}` : '';
}
